import { resolveSectionReferenceName } from '@washday/utilities';
import { DEFAULT_FOLIO_PREFIX } from '../../../constants';
import { IOutsourcedOrder } from '../../../hooks/outsourcingOrders/interfaces';
import { IOrder } from '../../../interfaces/Order';
import { IOrderProduct } from '../../../interfaces/Product';

export interface OutsourcingReportRow {
  id: string;
  outsourcedFolio: string;
  orderFolio: string;
  partnerName: string;
  status: string;
  sectionName: string;
  productName: string;
  quantity: number;
  pieces: number | null;
  sentDate?: string | Date;
  receivedDate?: string | Date;
  operationalNotes: string;
}

const columnIds = [
  'outsourcedFolio',
  'orderFolio',
  'partnerName',
  'status',
  'sectionName',
  'productName',
  'quantity',
  'pieces',
  'sentDate',
  'receivedDate',
  'operationalNotes',
] as const;

export const buildOutsourcingReportColumnIds = () => [...columnIds];

const resolvePartnerName = (partner: IOutsourcedOrder['partner']) => {
  if (!partner || typeof partner === 'string') return '';
  return partner.name || '';
};

const resolveOrderFolio = (order: IOutsourcedOrder['order']) => {
  if (!order || typeof order === 'string') return '';
  const prefix = order.folioPrefix || DEFAULT_FOLIO_PREFIX;
  return `${prefix}-${order.sequence || ''}`;
};

const resolvePieces = (product?: IOrderProduct) => {
  const pieceCount = Number(product?.pieceCount);
  if (Number.isFinite(pieceCount)) {
    return pieceCount;
  }

  const pieces = Number(product?.pieces);
  const quantity = Number(product?.quantity);
  if (
    Number.isFinite(pieces) &&
    Number.isFinite(quantity) &&
    product?.type !== 'weight' &&
    product?.type !== 'length'
  ) {
    return pieces * quantity;
  }

  return null;
};

const collectOperationalNotes = (order?: IOrder, product?: IOrderProduct) => {
  const notes: string[] = [];
  if (typeof order?.notes === 'string' && order.notes.trim()) {
    notes.push(order.notes.trim());
  }

  const attributes = Array.isArray(product?.attributes) ? product?.attributes : [];
  attributes.forEach((attribute: any) => {
    if (typeof attribute?.notes === 'string' && attribute.notes.trim()) {
      notes.push(attribute.notes.trim());
    }
  });

  return notes.join(' | ');
};

export const buildOutsourcingReportRows = (
  outsourcedOrders: IOutsourcedOrder[]
): OutsourcingReportRow[] =>
  outsourcedOrders.map((outsourcedOrder) => {
    const order = typeof outsourcedOrder.order === 'string'
      ? undefined
      : outsourcedOrder.order as IOrder;
    const product = outsourcedOrder.relatedProduct as IOrderProduct | undefined;

    return {
      id: outsourcedOrder._id,
      outsourcedFolio: `PM-${outsourcedOrder.outsourcedOrderSequence || ''}`,
      orderFolio: resolveOrderFolio(outsourcedOrder.order),
      partnerName: resolvePartnerName(outsourcedOrder.partner),
      status: outsourcedOrder.outsourcedStatus,
      sectionName: resolveSectionReferenceName(product?.section),
      productName: product?.name || '',
      quantity: Number(product?.quantity) || 0,
      pieces: resolvePieces(product),
      sentDate: outsourcedOrder.sentDate,
      receivedDate: outsourcedOrder.receivedDate,
      operationalNotes: collectOperationalNotes(order, product),
    };
  });
